import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

import {
  formatPercent,
  formatTonnage,
  getAgeingBgColor,
  getMetricTone,
  getSafeNumber,
  getSellerVmId,
  // getStatusTone,
  getToneColor,
} from "../makeData";
import { DEFAULT_SELLER_CARD_THRESHOLDS } from "../config";
import { pascalCase } from "utils";

const thresholds = DEFAULT_SELLER_CARD_THRESHOLDS;

const Metric = React.memo(({ value, label, color }) => (
  <Box>
    <Typography sx={{ color, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.1 }}>
      {label}
    </Typography>
  </Box>
));

const SellerCard = ({ seller = {}, checked = false, onChange }) => {
  const theme = useTheme();
  const {
    vmId,
    overdueOrders,
    maxAgeing,
    fgReadiness,
    ageingTone,
    fgReadinessTone,
    onTimeTone,
    inFullTone,
  } = useMemo(() => {
    const resolvedMaxAgeing = getSafeNumber(seller.maxAgeing);

    return {
      vmId: getSellerVmId(seller),
      overdueOrders: Number(seller.overdueOrders || 0),
      maxAgeing: resolvedMaxAgeing,
      fgReadiness: getSafeNumber(seller.fgReadiness),
      ageingTone: getMetricTone(resolvedMaxAgeing, thresholds.maxAgeing),
      // fgReadinessTone: seller.fgReadinessStatus
      //   ? getStatusTone(seller.fgReadinessStatus, thresholds.fgReadiness)
      //   : "red",
      fgReadinessTone: getMetricTone(
        seller.fgReadiness,
        thresholds.fgReadiness,
      ),
      onTimeTone: getMetricTone(seller.onTime, thresholds.onTime),
      inFullTone: getMetricTone(seller.inFull, thresholds.inFull),
    };
  }, [seller]);

  const handleCardClick = useCallback(() => {
    onChange(vmId);
  }, [onChange, vmId]);

  const handleCheckboxClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleCheckboxChange = useCallback(() => {
    onChange(vmId);
  }, [onChange, vmId]);

  return (
    <Paper
      elevation={-1}
      onClick={handleCardClick}
      sx={{
        p: 2,
        pt: 3,
        pb: 3,
        mb: 2,
        position: "relative",
        border: "1px solid",
        borderColor: theme.palette.grey[300],
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: checked
          ? theme.palette.info.lightBlueBackround
          : theme.palette.common.white,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "3px",
          backgroundColor: checked
            ? theme.palette.primary.main
            : theme.palette.grey[400],
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <Checkbox
          checked={checked}
          onClick={handleCheckboxClick}
          onChange={handleCheckboxChange}
          size="small"
          sx={{ p: 0 }}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: 14,
              mt: 0.25,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {pascalCase(seller.companyName)}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.grey[1000],
              fontSize: 12,
              lineHeight: 1.5,
              mt: 0.25,
            }}
          >
            {vmId}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right", mt: -1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 30,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              color: getToneColor(theme, ageingTone),
              backgroundColor: getAgeingBgColor(theme, ageingTone),
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {maxAgeing}d
          </Box>
          <Typography
            sx={{ color: theme.palette.grey[1000], fontSize: 12, mt: 0.25 }}
          >
            MAX AGEING
          </Typography>
          {overdueOrders > 0 && (
            <>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 18,
                  mt: 0.5,
                  border: "1px solid",
                  borderColor: theme.palette.error.light,
                  borderRadius: 0.5,
                  color: theme.palette.error.light,
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {overdueOrders}
              </Box>
              <Typography
                sx={{
                  color: theme.palette.error.light,
                  fontSize: 12,
                  mt: 0.25,
                }}
              >
                OVERDUE ORDERS
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            sx={{
              color: theme.palette.grey[1000],
              fontSize: 12,
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            FG READINESS
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(fgReadiness, 100)}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 4,
              backgroundColor: theme.palette.grey[300],
              "& .MuiLinearProgress-bar": {
                backgroundColor: getToneColor(theme, fgReadinessTone),
              },
            }}
          />
          <Typography
            sx={{
              color: getToneColor(theme, fgReadinessTone),
              fontSize: 14,
              fontWeight: 700,
              minWidth: 28,
              textAlign: "right",
            }}
          >
            {formatPercent(seller.fgReadiness)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          rowGap: 2,
          mt: 2,
        }}
      >
        <Metric
          value={formatTonnage(seller.tonnageDue)}
          label="TONNAGE DUE"
          color={theme.palette.text.primary}
        />
        <Metric
          value={seller.openOrders}
          label="OPEN ORDERS"
          color={theme.palette.text.primary}
        />
        <Metric
          value={formatPercent(seller.onTime)}
          label="ONTIME"
          color={getToneColor(theme, onTimeTone)}
        />
        <Metric
          value={formatPercent(seller.inFull)}
          label="IN FULL"
          color={getToneColor(theme, inFullTone)}
        />
      </Box>
    </Paper>
  );
};

SellerCard.propTypes = {
  seller: PropTypes.shape({
    vmId: PropTypes.string,
    VmId: PropTypes.string,
    companyName: PropTypes.string,
    maxAgeing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fgReadiness: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fgReadinessStatus: PropTypes.string,
    overdueOrders: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    openOrders: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tonnageDue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    inFull: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default React.memo(SellerCard);
